import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuthStore } from '@/store';
import { api } from '@/services/api';
import {
  CLASS_DATA,
  RACE_NAMES,
  ALIGNMENT_NAMES,
  getModifier,
  calculateMaxHP,
} from '@/types';
import type { CharacterClass, Race, Alignment } from '@/types';

const CLASS_EMOJIS: Record<CharacterClass, string> = {
  fighter: '⚔️',
  wizard: '🔮',
  cleric: '✝️',
  thief: '🗡️',
  ranger: '🏹',
  bard: '🎵',
  druid: '🌿',
  paladin: '🛡️',
  barbarian: '💪',
  immolator: '🔥',
};

// Races strictly by Dungeon World rules
const CLASS_RACES: Record<CharacterClass, Race[]> = {
  fighter: ['human', 'elf', 'dwarf', 'halfling'],
  wizard: ['human', 'elf'],
  cleric: ['human', 'dwarf'],
  thief: ['human', 'halfling'],
  ranger: ['human', 'elf'],
  bard: ['human', 'elf'],
  druid: ['human', 'elf', 'halfling'],
  paladin: ['human'],
  barbarian: ['human'],
  immolator: ['human', 'salamander'],
};

// Dungeon World standard modifiers: +2, +1, +1, 0, 0, -1 distributed on base 10
const MODIFIER_ARRAY = [2, 1, 1, 0, 0, -1];
const BASE_STAT = 10;

const STATS: { key: string; name: string; nameRu: string }[] = [
  { key: 'strength', name: 'STR', nameRu: 'СИЛ' },
  { key: 'dexterity', name: 'DEX', nameRu: 'ЛОВ' },
  { key: 'constitution', name: 'CON', nameRu: 'ТЕЛ' },
  { key: 'intelligence', name: 'INT', nameRu: 'ИНТ' },
  { key: 'wisdom', name: 'WIS', nameRu: 'МДР' },
  { key: 'charisma', name: 'CHA', nameRu: 'ХАР' },
];

type Gender = 'male' | 'female';
const GENDER_NAMES: Record<Gender, string> = {
  male: 'Мужской',
  female: 'Женский',
};

type Step = 'name' | 'gender' | 'class' | 'race' | 'stats' | 'alignment' | 'confirm';

export function CharacterCreationPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { setCharacter, character: existingCharacter } = useAuthStore();

  const [step, setStep] = useState<Step>('name');
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [characterClass, setCharacterClass] = useState<CharacterClass | null>(null);
  const [race, setRace] = useState<Race | null>(null);
  // Stats now store modifiers, not absolute values
  const [statModifiers, setStatModifiers] = useState<Record<string, number | null>>({
    strength: null,
    dexterity: null,
    constitution: null,
    intelligence: null,
    wisdom: null,
    charisma: null,
  });
  const [availableModifiers, setAvailableModifiers] = useState<number[]>([...MODIFIER_ARRAY]);
  const [alignment, setAlignment] = useState<Alignment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps: Step[] = ['name', 'gender', 'class', 'race', 'stats', 'alignment', 'confirm'];

  // Calculate final stat value from modifier
  const getStatValue = (modifier: number | null): number => {
    if (modifier === null) return 0;
    return BASE_STAT + modifier;
  };

  // Get all stats as absolute values for API
  const getStatsForApi = () => ({
    strength: getStatValue(statModifiers.strength),
    dexterity: getStatValue(statModifiers.dexterity),
    constitution: getStatValue(statModifiers.constitution),
    intelligence: getStatValue(statModifiers.intelligence),
    wisdom: getStatValue(statModifiers.wisdom),
    charisma: getStatValue(statModifiers.charisma),
  });
  const currentStepIndex = steps.indexOf(step);

  const canProceed = () => {
    switch (step) {
      case 'name': return name.trim().length >= 2;
      case 'gender': return gender !== null;
      case 'class': return characterClass !== null;
      case 'race': return race !== null;
      case 'stats': return availableModifiers.length === 0;
      case 'alignment': return alignment !== null;
      default: return true;
    }
  };

  const nextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      hapticFeedback('light');
      setStep(steps[currentStepIndex + 1]);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      hapticFeedback('light');
      setStep(steps[currentStepIndex - 1]);
    } else {
      navigate('/mode-select');
    }
  };

  const assignModifier = (statKey: string, modifier: number) => {
    hapticFeedback('light');

    // If stat already has a modifier, return it to available
    if (statModifiers[statKey] !== null) {
      setAvailableModifiers(prev => [...prev, statModifiers[statKey]!].sort((a, b) => b - a));
    }

    // Assign new modifier
    setStatModifiers(prev => ({ ...prev, [statKey]: modifier }));
    setAvailableModifiers(prev => {
      const idx = prev.indexOf(modifier);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearModifier = (statKey: string) => {
    hapticFeedback('light');
    if (statModifiers[statKey] !== null) {
      setAvailableModifiers(prev => [...prev, statModifiers[statKey]!].sort((a, b) => b - a));
      setStatModifiers(prev => ({ ...prev, [statKey]: null }));
    }
  };

  const handleCreateClick = () => {
    createCharacter();
  };

  const createCharacter = async () => {
    if (!characterClass || !race || !alignment || !gender) return;

    setIsLoading(true);
    setError(null);
    hapticFeedback('medium');

    const finalStats = getStatsForApi();

    try {
      const response = await api.post<{ character: typeof existingCharacter }>('/characters', {
        name: name.trim(),
        characterClass,
        race,
        alignment,
        gender,
        ...finalStats,
      });

      if (response.character) {
        setCharacter(response.character);
        hapticFeedback('success');
        navigate('/');
      } else {
        setError('Не удалось создать персонажа');
        hapticFeedback('error');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка соединения';
      setError(errorMessage);
      hapticFeedback('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={prevStep}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Создание персонажа</h1>
          <div className="w-16" />
        </div>

        {/* Progress */}
        <div className="flex gap-1">
          {steps.map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full ${
                i <= currentStepIndex ? 'bg-amber-500' : 'bg-gray-700'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 overflow-y-auto">
        <AnimatePresence mode="wait">
          {/* Step 1: Name */}
          {step === 'name' && (
            <motion.div
              key="name"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <span className="text-5xl mb-4 block">📜</span>
                <h2 className="text-xl font-bold text-white">Как зовут героя?</h2>
                <p className="text-gray-400 text-sm mt-2">
                  Выберите имя для своего персонажа
                </p>
              </div>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Введите имя..."
                maxLength={30}
                className="w-full p-4 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none text-center text-lg"
              />
            </motion.div>
          )}

          {/* Step 2: Gender */}
          {step === 'gender' && (
            <motion.div
              key="gender"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <span className="text-5xl mb-4 block">⚔️</span>
                <h2 className="text-xl font-bold text-white">Выберите пол</h2>
                <p className="text-gray-400 text-sm mt-2">
                  Пол вашего персонажа
                </p>
              </div>

              <div className="space-y-3">
                {(['male', 'female'] as Gender[]).map((g) => (
                  <button
                    key={g}
                    onClick={() => {
                      hapticFeedback('light');
                      setGender(g);
                    }}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      gender === g
                        ? 'bg-amber-500/20 border-2 border-amber-500'
                        : 'bg-gray-800/50 border border-gray-700/50'
                    }`}
                  >
                    <p className="font-medium text-white flex items-center gap-3">
                      <span className="text-2xl">{g === 'male' ? '♂️' : '♀️'}</span>
                      {GENDER_NAMES[g]}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Class */}
          {step === 'class' && (
            <motion.div
              key="class"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">Выберите класс</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Класс определяет способности и стиль игры
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(Object.keys(CLASS_DATA) as CharacterClass[]).map((cls) => (
                  <button
                    key={cls}
                    onClick={() => {
                      hapticFeedback('light');
                      setCharacterClass(cls);
                      setRace(null); // Reset race when class changes
                    }}
                    className={`p-4 rounded-xl text-left transition-all ${
                      characterClass === cls
                        ? 'bg-amber-500/20 border-2 border-amber-500'
                        : 'bg-gray-800/50 border border-gray-700/50'
                    }`}
                  >
                    <span className="text-2xl block mb-2">{CLASS_EMOJIS[cls]}</span>
                    <p className="font-medium text-white">{CLASS_DATA[cls].nameRu}</p>
                    <p className="text-xs text-gray-400">
                      HP: {CLASS_DATA[cls].baseHP}+ТЕЛ • {CLASS_DATA[cls].damageDie}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Race */}
          {step === 'race' && characterClass && (
            <motion.div
              key="race"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">Выберите расу</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Раса даёт особую способность
                </p>
              </div>

              <div className="space-y-3">
                {CLASS_RACES[characterClass].map((r) => (
                  <button
                    key={r}
                    onClick={() => {
                      hapticFeedback('light');
                      setRace(r);
                    }}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      race === r
                        ? 'bg-amber-500/20 border-2 border-amber-500'
                        : 'bg-gray-800/50 border border-gray-700/50'
                    }`}
                  >
                    <p className="font-medium text-white">{RACE_NAMES[r]}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 5: Stats */}
          {step === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-white">Распределите модификаторы</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Базовое значение: 10. Распределите модификаторы.
                </p>
              </div>

              {/* Available modifiers */}
              <div className="flex justify-center gap-2 mb-6">
                {availableModifiers.map((mod, index) => (
                  <div
                    key={`${mod}-${index}`}
                    className={`w-12 h-10 rounded-lg border flex items-center justify-center font-bold ${
                      mod > 0
                        ? 'bg-green-500/20 border-green-500/50 text-green-400'
                        : mod < 0
                          ? 'bg-red-500/20 border-red-500/50 text-red-400'
                          : 'bg-gray-500/20 border-gray-500/50 text-gray-400'
                    }`}
                  >
                    {mod > 0 ? `+${mod}` : mod}
                  </div>
                ))}
                {availableModifiers.length === 0 && (
                  <p className="text-green-400 text-sm">✓ Все распределены</p>
                )}
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-3">
                {STATS.map((stat) => (
                  <div
                    key={stat.key}
                    className="p-3 rounded-xl bg-gray-800/50 border border-gray-700/50"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-sm">{stat.nameRu}</span>
                      {statModifiers[stat.key] !== null && (
                        <button
                          onClick={() => clearModifier(stat.key)}
                          className="text-gray-500 text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {statModifiers[stat.key] !== null ? (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{getStatValue(statModifiers[stat.key])}</p>
                        <p className={`text-sm ${
                          statModifiers[stat.key]! > 0
                            ? 'text-green-400'
                            : statModifiers[stat.key]! < 0
                              ? 'text-red-400'
                              : 'text-gray-400'
                        }`}>
                          {statModifiers[stat.key]! > 0 ? `+${statModifiers[stat.key]}` : statModifiers[stat.key]}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {availableModifiers.map((mod, index) => (
                          <button
                            key={`${stat.key}-${mod}-${index}`}
                            onClick={() => assignModifier(stat.key, mod)}
                            className={`px-2 py-1 text-sm rounded ${
                              mod > 0
                                ? 'bg-green-900/50 text-green-300 hover:bg-green-500/30'
                                : mod < 0
                                  ? 'bg-red-900/50 text-red-300 hover:bg-red-500/30'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {mod > 0 ? `+${mod}` : mod}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 5: Alignment */}
          {step === 'alignment' && (
            <motion.div
              key="alignment"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">Выберите мировоззрение</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Мировоззрение определяет моральный компас героя
                </p>
              </div>

              <div className="space-y-3">
                {(['lawful', 'good', 'neutral', 'chaotic'] as Alignment[]).map((a) => (
                  <button
                    key={a}
                    onClick={() => {
                      hapticFeedback('light');
                      setAlignment(a);
                    }}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      alignment === a
                        ? 'bg-amber-500/20 border-2 border-amber-500'
                        : 'bg-gray-800/50 border border-gray-700/50'
                    }`}
                  >
                    <p className="font-medium text-white">{ALIGNMENT_NAMES[a]}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 7: Confirm */}
          {step === 'confirm' && characterClass && race && alignment && gender && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <span className="text-5xl block mb-4">{CLASS_EMOJIS[characterClass]}</span>
                <h2 className="text-2xl font-bold text-white">{name}</h2>
                <p className="text-amber-400">
                  {RACE_NAMES[race]} {CLASS_DATA[characterClass].nameRu}
                </p>
                <p className="text-gray-400 text-sm">{GENDER_NAMES[gender]} • {ALIGNMENT_NAMES[alignment]}</p>
              </div>

              {/* Stats summary */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {STATS.map((stat) => {
                  const finalValue = getStatValue(statModifiers[stat.key]);
                  const mod = statModifiers[stat.key] ?? 0;
                  return (
                    <div key={stat.key} className="p-2 rounded-lg bg-gray-800/50 text-center">
                      <p className="text-gray-400 text-xs">{stat.nameRu}</p>
                      <p className="text-white font-bold">{finalValue}</p>
                      <p className={`text-xs ${mod > 0 ? 'text-green-400' : mod < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {mod > 0 ? `+${mod}` : mod}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Derived stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-red-900/30 text-center">
                  <p className="text-red-400 text-xs">HP</p>
                  <p className="text-white font-bold">
                    {calculateMaxHP(characterClass, getStatValue(statModifiers.constitution))}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-blue-900/30 text-center">
                  <p className="text-blue-400 text-xs">Урон</p>
                  <p className="text-white font-bold">{CLASS_DATA[characterClass].damageDie}</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-900/30 text-center">
                  <p className="text-yellow-400 text-xs">Нагрузка</p>
                  <p className="text-white font-bold">
                    {CLASS_DATA[characterClass].baseLoad + getModifier(getStatValue(statModifiers.strength))}
                  </p>
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800">
        {step === 'confirm' ? (
          <button
            onClick={handleCreateClick}
            disabled={isLoading}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold disabled:opacity-50"
          >
            {isLoading ? 'Создание...' : 'Создать персонажа'}
          </button>
        ) : (
          <button
            onClick={nextStep}
            disabled={!canProceed()}
            className="w-full py-4 rounded-xl bg-amber-500 text-black font-bold disabled:opacity-50 disabled:bg-gray-700 disabled:text-gray-500"
          >
            Далее
          </button>
        )}
      </div>
    </div>
  );
}
